import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Hero from '../components/Hero';

const Home = () => {
  return (
    <>
      <Head>
        <title>KTR Capital — Investing in Science &amp; Technology</title>
        <meta
          name="description"
          content="KTR Capital is a private investment firm offering accredited investors institutional access across biotech, quantitative trading, and private equity. Austin, Texas."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/svg+xml" href="/ktr_isotipo.svg" />
        <meta name="robots" content="noindex" />
      </Head>

      <Navbar />
      <Hero />

      {/* Three Pillars */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">The Platform</div>
            <h2 className="text-3xl md:text-4xl font-light mb-6 leading-tight">
              A diversified platform, one capital base.
            </h2>
            <p className="text-lg text-gray-400 font-light leading-relaxed">
              KTR Capital is built around a physician-led biotech edge and proprietary trading
              technology, with a diversified platform spanning biotech, quantitative trading, and
              private equity. Each vertical generates independent value within a single fund.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-gray-900">
            <div className="bg-black p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">HELIOS</div>
              <h3 className="text-xl font-light text-white mb-3">Quantitative Trading</h3>
              <p className="text-sm text-gray-400 leading-relaxed font-light">
                A proprietary automated options strategy trading SPY and QQQ by reading the
                structural mechanics of institutional order flow. Built and operated entirely
                in-house on institutional-grade market data and execution infrastructure.
              </p>
            </div>

            <div className="bg-black p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">KTR Institute</div>
              <h3 className="text-xl font-light text-white mb-3">Research &amp; Development</h3>
              <p className="text-sm text-gray-400 leading-relaxed font-light">
                Longer-horizon applied research and the intellectual property that supports the
                broader platform, including advanced signal-processing methods that strengthen the
                firm's trading and analytics capabilities.
              </p>
            </div>

            <div className="bg-black p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">Strandwise</div>
              <h3 className="text-xl font-light text-white mb-3">Biotechnology</h3>
              <p className="text-sm text-gray-400 leading-relaxed font-light">
                A private-equity position in Strandwise, a biotech company developing implantable
                biosensor technology for continuous, post-surgical cancer monitoring — built on a
                physician-led understanding of the clinical need.
              </p>
            </div>
          </div>

          <div className="mt-10">
            <Link
              href="/platform"
              className="text-sm text-orange-400 hover:text-orange-300 font-light inline-flex items-center gap-2"
            >
              Read about the platform architecture
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Principal */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-1">
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">The Firm</div>
              <h2 className="text-3xl font-light mb-2">About KTR Capital</h2>
              <p className="text-sm text-gray-400 font-light">Private Investment Firm</p>
              <p className="text-sm text-gray-500 font-light mt-1">Austin, Texas</p>
            </div>
            <div className="md:col-span-2 space-y-6 text-gray-300 font-light leading-relaxed">
              <p>
                KTR Capital is a private investment firm that combines a physician-led biotech edge with
                proprietary trading technology. Our approach applies deep clinical and scientific
                expertise to public-market biotech, alongside systematic, in-house quantitative
                strategies — a discipline built on measurement, reproducibility, and risk management.
              </p>
              <p>
                Through KTR Capital Fund I, accredited investors gain a single, diversified allocation
                across the firm's strategies: a long/short oncology biotech portfolio, the HELIOS
                quantitative trading system, and a private-equity position in Strandwise. The firm also
                operates real commercial businesses, generating revenue alongside investment returns.
              </p>
              <p>
                <Link href="/about" className="text-orange-400 hover:text-orange-300 text-sm">
                  More about KTR Capital →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA — Sign up / Sign in */}
      <section className="bg-black text-white py-24 px-6 border-t border-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">For Qualified Investors</div>
          <h2 className="text-3xl md:text-4xl font-light mb-6 leading-tight">
            Begin your investor qualification.
          </h2>
          <p className="text-lg text-gray-400 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
            We work with institutional allocators, family offices, and qualified individual
            investors. Create an account to begin qualification and access the platform. Existing
            investors can sign in to their portal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              href="/sign-up"
              className="inline-flex items-center gap-3 bg-orange-500 text-black px-8 py-4 rounded font-medium hover:bg-orange-600 transition-colors w-full sm:w-auto justify-center"
            >
              Sign up
              <span aria-hidden="true">→</span>
            </Link>
            <Link
              href="/sign-in"
              className="inline-flex items-center gap-3 border border-gray-700 text-gray-200 px-8 py-4 rounded font-medium hover:bg-gray-900 transition-colors w-full sm:w-auto justify-center"
            >
              Investor sign in
            </Link>
          </div>
          <p className="text-xs text-gray-600 font-light mt-8 max-w-2xl mx-auto leading-relaxed">
            KTR Capital Fund I is offered only to accredited investors. Creating an account begins a
            qualification process; it is not an offer to sell or a solicitation to buy securities.
            Investor eligibility is verified before any investment.
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Home;
